import {Component, Input, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {FormControl, FormGroup} from "@angular/forms";
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {environment} from "@environment";
import {
  adjustForTimezone,
  alphabetical,
  email,
  max_length_10,
  max_length_100,
  max_length_255,
  max_length_30,
  max_length_45,
  max_length_50,
  mediumTextValidator,
  required
} from '@utility';
import {AbstractComponent} from "@components";
import {Country, Lookups, Message, UserDetail} from "@model";
import {TOAST_STATE, ToastService, UserDetailService} from "@services";
import {USER_PROFILE_VIEW_ROUTE} from "src/app/app-routing.module";

@Component({
  selector: 'app-user-profile-edit',
  templateUrl: './user-profile-edit.component.html',
  styleUrls: ['./user-profile-edit.component.scss'],
})
export class UserProfileEditComponent extends AbstractComponent implements OnInit {

  @Input() countries: Country[] = [];
  @Input() genders: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input('sizes') tshirtSizes: Lookups[] = [];
  @Input() unreadMessages: Message[] | undefined;

  currentDate: Date = new Date();

  currentUserDetails: UserDetail | undefined;
  userProfileComponentState: UserProfileComponentState = UserProfileComponentState.INITIAL;
  userProfileComponentStateType = UserProfileComponentState;
  editProfilePictureMode: boolean = false;
  profilePicture: string | undefined;

  inputForm = new FormGroup({
    firstname: new FormControl(),
    lastname: new FormControl(),
    gender: new FormControl(),
    city: new FormControl(),
    country: new FormControl(),
    birthDay: new FormControl(),
    phoneNumber: new FormControl(),
    tshirtSize: new FormControl(),
    address: new FormControl(),
    email: new FormControl(),
    zip: new FormControl(),
    picture: new FormControl(),
    category: new FormControl(),
    identityCardNumber: new FormControl(),
    identityCardIssuer: new FormControl(),
    identityCardExpires: new FormControl(),
    club: new FormControl(),
    emergencyPhone: new FormControl(),
    meal: new FormControl(),
    nickname: new FormControl()
  });

  constructor(private userProfile: UserDetailService, private toastService: ToastService, private router: Router,
              private translocoService: TranslocoService, public formatter: NgbDateParserFormatter,
              private userDetailService:UserDetailService) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.fetchUserProfile();
  }

  private initForm() {
    this.inputForm.controls.firstname.addValidators([required, max_length_45, alphabetical]);
    this.inputForm.controls.lastname.addValidators([required, max_length_45, alphabetical]);
    this.inputForm.controls.birthDay.addValidators([required]);
    this.inputForm.controls.city.addValidators([max_length_45, alphabetical]);
    this.inputForm.controls.club.addValidators([max_length_100]);
    this.inputForm.controls.address.addValidators([max_length_50]);
    this.inputForm.controls.email.addValidators([required, email]);
    this.inputForm.controls.email.disable();
    this.inputForm.controls.zip.addValidators([max_length_10]);
    this.inputForm.controls.nickname.addValidators([max_length_30]);
    this.inputForm.controls.picture.addValidators([mediumTextValidator]);
    this.inputForm.controls.identityCardNumber.addValidators([max_length_30]);
    this.inputForm.controls.identityCardIssuer.addValidators([max_length_255]);
  }

  private transformDate(initalDate: Date): Date {
    let date: Date = new Date(initalDate);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private fetchUserProfile(): void {
    this.subscriptions.push(this.userDetailService.currentUserDetails.subscribe((userDetails: UserDetail | undefined) => {
      this.currentUserDetails = userDetails;
      if (this.currentUserDetails?.picture) this.profilePicture = this.currentUserDetails.picture;
      if (this.currentUserDetails?.birthDay) {
        this.currentUserDetails!.birthDay = this.transformDate(this.currentUserDetails.birthDay);
      }
      if (this.currentUserDetails?.identityCardExpires) {
        this.currentUserDetails!.identityCardExpires = this.transformDate(this.currentUserDetails.identityCardExpires);
      }
      if (this.currentUserDetails) {
        this.inputForm.patchValue(this.currentUserDetails);
      }
    }));
  }

  processFile(imageInput: any): void {
    if (imageInput.files?.length > 0) {
      const file: File = imageInput.files[0];
      if (file.size > environment.maxFileSize) {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.user.profile.max.size.error"));
      } else {
        const reader: FileReader = new FileReader();
        reader.addEventListener('load', (event: any) => {
          this.profilePicture = event.target.result;
        });
        reader.readAsDataURL(file);
      }
    }
    this.editProfilePictureMode = !this.editProfilePictureMode;
  }

  onEditProfilePicture(): void {
    this.editProfilePictureMode = !this.editProfilePictureMode;
  }

  onDeleteProfilePicture(): void {
    this.editProfilePictureMode = !this.editProfilePictureMode;
    this.profilePicture = undefined;
  }

  getUserDetails(form: FormGroup): UserDetail {
    Object.keys(form.controls).forEach((key: string) => {
      if (form.controls[key].value === "") {
        form.controls[key].setValue(undefined);
      }
    });
    let userDetails: UserDetail = {...this.currentUserDetails}
    userDetails.address = form.value.address;
    userDetails.country = form.value.country;
    userDetails.email = this.currentUserDetails?.email;
    userDetails.firstname = form.value.firstname;
    userDetails.gender = form.value.gender;
    userDetails.height = form.value.height;
    userDetails.lastname = form.value.lastname;
    userDetails.phoneNumber = form.value.phoneNumber;
    userDetails.city = form.value.city;
    userDetails.tshirtSize = form.value.tshirtSize;
    userDetails.weight = form.value.weight;
    userDetails.picture = this.profilePicture;
    userDetails.identityCardIssuer = form.value.identityCardIssuer;
    userDetails.identityCardNumber = form.value.identityCardNumber;
    userDetails.emergencyPhone = form.value.emergencyPhone;
    userDetails.meal = form.value.meal;
    userDetails.transport = form.value.transport;
    userDetails.club = form.value.club;
    userDetails.zip = form.value.zip;
    userDetails.footwearSize = form.value.footwearSize;
    userDetails.nickname = form.value.nickname;
    if (form.value.birthDay) {
      userDetails.birthDay = adjustForTimezone(this.transformDate(form.value.birthDay));
    } else {
      userDetails.birthDay = undefined;
    }
    if (form.value.identityCardExpires) {
      userDetails.identityCardExpires = adjustForTimezone(this.transformDate(form.value.identityCardExpires));
    } else {
      userDetails.identityCardExpires = undefined;
    }
    return userDetails;
  }

  onUpdateProfile(): void {
    if (!this.inputForm.valid) {
      for (const control in this.inputForm.controls) {
        this.inputForm.get(control)?.markAsTouched();
      }
      return;
    } else {
      if (this.currentUserDetails !== undefined) {
        let userDetails: UserDetail = this.getUserDetails(this.inputForm);
        this.userProfile.updateUserProfile(userDetails, () => {
          this.toProfilePage();
        }, () => {
        });
      }
    }
  }

  getMinDateForBirthday(): Date {
    const minDate = new Date(this.currentDate);
    minDate.setFullYear(this.currentDate.getFullYear() - 110);
    return minDate;
  }

  getMinDateForExpireCard(): Date {
    return this.currentDate;
  }

  toProfilePage(): void {
    this.router.navigate([USER_PROFILE_VIEW_ROUTE]);
  }
}

enum UserProfileComponentState {
  INITIAL,
  SUCCESS,
  ERROR,
}
